/**
 * TomorrowActionsCard - The "3 Things for Tomorrow" feature
 * Set 3 small actions for tomorrow that align with your future self
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { 
  Target, Sparkles, ChevronRight, Check, X, 
  AlertCircle, Lightbulb, ArrowRight
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { format, addDays, isToday, parseISO } from 'date-fns';

interface TomorrowActionsCardProps {
  mode: 'set' | 'review';
  actions?: {
    action_1: string | null;
    action_2: string | null;
    action_3: string | null;
    action_1_completed?: boolean;
    action_2_completed?: boolean;
    action_3_completed?: boolean;
    action_1_barrier?: string | null;
    action_2_barrier?: string | null;
    action_3_barrier?: string | null;
    future_self_connection?: string | null;
  };
  onSave: (data: any) => void;
  futureSelfIdentity?: string;
}

const ACTION_PROMPTS = [
  "One small step toward your health...",
  "Something that moves a project forward...",
  "A kind gesture for yourself or others..."
];

export const TomorrowActionsCard: React.FC<TomorrowActionsCardProps> = ({
  mode,
  actions,
  onSave,
  futureSelfIdentity
}) => {
  const [action1, setAction1] = useState(actions?.action_1 || '');
  const [action2, setAction2] = useState(actions?.action_2 || '');
  const [action3, setAction3] = useState(actions?.action_3 || '');
  const [action1Completed, setAction1Completed] = useState(actions?.action_1_completed || false);
  const [action2Completed, setAction2Completed] = useState(actions?.action_2_completed || false);
  const [action3Completed, setAction3Completed] = useState(actions?.action_3_completed || false);
  const [barrier1, setBarrier1] = useState(actions?.action_1_barrier || '');
  const [barrier2, setBarrier2] = useState(actions?.action_2_barrier || '');
  const [barrier3, setBarrier3] = useState(actions?.action_3_barrier || '');
  const [futureSelfConnection, setFutureSelfConnection] = useState(actions?.future_self_connection || '');
  const [showBarrierInputs, setShowBarrierInputs] = useState(false);

  useEffect(() => {
    if (actions) {
      setAction1(actions.action_1 || '');
      setAction2(actions.action_2 || '');
      setAction3(actions.action_3 || '');
      setAction1Completed(actions.action_1_completed || false);
      setAction2Completed(actions.action_2_completed || false);
      setAction3Completed(actions.action_3_completed || false);
      setBarrier1(actions.action_1_barrier || '');
      setBarrier2(actions.action_2_barrier || '');
      setBarrier3(actions.action_3_barrier || '');
      setFutureSelfConnection(actions.future_self_connection || '');
    }
  }, [actions]);

  const handleSave = () => {
    onSave({
      action_1: action1 || null,
      action_2: action2 || null,
      action_3: action3 || null,
      action_1_completed: action1Completed,
      action_2_completed: action2Completed,
      action_3_completed: action3Completed,
      action_1_barrier: barrier1 || null,
      action_2_barrier: barrier2 || null,
      action_3_barrier: barrier3 || null,
      future_self_connection: futureSelfConnection || null,
    });
  };

  const completedCount = [action1Completed, action2Completed, action3Completed].filter(Boolean).length;
  const totalActions = [action1, action2, action3].filter(Boolean).length;

  if (mode === 'set') {
    return (
      <Card className="border-primary/20 bg-gradient-to-br from-primary/5 via-background to-accent/5">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
                <Target className="w-4 h-4 text-primary" />
              </div>
              <div>
                <CardTitle className="text-base font-medium">Tomorrow's 3 Actions</CardTitle>
                <p className="text-xs text-muted-foreground">Small steps toward your future self</p>
              </div>
            </div>
            <Badge variant="outline" className="text-xs">
              {format(addDays(new Date(), 1), 'EEE, MMM d')}
            </Badge>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Future Self Connection */}
          {futureSelfIdentity && (
            <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
              <div className="flex items-start gap-2">
                <Sparkles className="w-4 h-4 text-primary mt-0.5" />
                <div>
                  <p className="text-xs font-medium text-primary">Your Future Self</p>
                  <p className="text-xs text-muted-foreground">{futureSelfIdentity}</p>
                </div>
              </div>
            </div>
          )}

          {/* Action Inputs */}
          <div className="space-y-3">
            {[
              { value: action1, setValue: setAction1, prompt: ACTION_PROMPTS[0], num: 1 },
              { value: action2, setValue: setAction2, prompt: ACTION_PROMPTS[1], num: 2 },
              { value: action3, setValue: setAction3, prompt: ACTION_PROMPTS[2], num: 3 },
            ].map(({ value, setValue, prompt, num }) => (
              <div key={num} className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
                  <span className="text-xs font-medium text-primary">{num}</span>
                </div>
                <Input
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder={prompt}
                  className="pl-12 h-11 bg-background/50"
                />
              </div>
            ))}
          </div>

          {/* How This Connects */}
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
              How do these connect to your future self?
            </label>
            <Textarea
              value={futureSelfConnection}
              onChange={(e) => setFutureSelfConnection(e.target.value)}
              placeholder="These actions will help me become..."
              className="min-h-[60px] text-sm bg-background/50"
            />
          </div>

          <Button onClick={handleSave} className="w-full gap-2">
            <Check className="w-4 h-4" />
            Set Tomorrow's Actions
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Review Mode
  return (
    <Card className="border-accent/20 bg-gradient-to-br from-accent/5 via-background to-secondary/5">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
              <Target className="w-4 h-4 text-accent" />
            </div>
            <div>
              <CardTitle className="text-base font-medium">Yesterday's Actions</CardTitle>
              <p className="text-xs text-muted-foreground">How did you do?</p>
            </div>
          </div>
          <Badge 
            variant={completedCount === totalActions ? "default" : "outline"}
            className={cn(
              "text-xs",
              completedCount === totalActions && "bg-primary"
            )}
          >
            {completedCount}/{totalActions} completed
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Action Review Items */}
        <div className="space-y-3">
          {[
            { action: action1, completed: action1Completed, setCompleted: setAction1Completed, barrier: barrier1, setBarrier: setBarrier1, num: 1 },
            { action: action2, completed: action2Completed, setCompleted: setAction2Completed, barrier: barrier2, setBarrier: setBarrier2, num: 2 },
            { action: action3, completed: action3Completed, setCompleted: setAction3Completed, barrier: barrier3, setBarrier: setBarrier3, num: 3 },
          ].filter(item => item.action).map(({ action, completed, setCompleted, barrier, setBarrier, num }) => (
            <motion.div 
              key={num}
              className={cn(
                "p-3 rounded-lg border transition-all",
                completed 
                  ? "bg-primary/5 border-primary/30" 
                  : "bg-muted/30 border-border/50"
              )}
            >
              <div className="flex items-start gap-3">
                <div 
                  className="cursor-pointer mt-0.5"
                  onClick={() => setCompleted(!completed)}
                >
                  <Checkbox 
                    checked={completed}
                    className={cn(
                      "w-5 h-5 rounded-full",
                      completed && "bg-primary border-primary"
                    )}
                  />
                </div>
                <div className="flex-1">
                  <p className={cn(
                    "text-sm",
                    completed && "line-through text-muted-foreground"
                  )}>
                    {action}
                  </p>
                  
                  {/* Barrier input for incomplete items */}
                  <AnimatePresence>
                    {!completed && showBarrierInputs && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-2"
                      >
                        <Input
                          value={barrier}
                          onChange={(e) => setBarrier(e.target.value)}
                          placeholder="What got in the way?"
                          className="text-xs h-8 bg-background/50"
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Show barrier inputs toggle */}
        {completedCount < totalActions && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowBarrierInputs(!showBarrierInputs)}
            className="w-full text-xs text-muted-foreground"
          >
            <AlertCircle className="w-3 h-3 mr-1" />
            {showBarrierInputs ? "Hide barrier questions" : "What got in the way?"}
          </Button>
        )}

        {/* Encouragement */}
        {completedCount > 0 && (
          <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
            <div className="flex items-start gap-2">
              <Lightbulb className="w-4 h-4 text-primary mt-0.5" />
              <p className="text-xs text-muted-foreground">
                {completedCount === totalActions 
                  ? "Amazing! You followed through on all your intentions. Your future self is closer than ever."
                  : `Great progress! You completed ${completedCount} action${completedCount > 1 ? 's' : ''}. Every step counts.`
                }
              </p>
            </div>
          </div>
        )}

        <Button onClick={handleSave} className="w-full gap-2">
          <ArrowRight className="w-4 h-4" />
          Save & Continue
        </Button>
      </CardContent>
    </Card>
  );
};
